import type { AppError } from '@/features/auth/types';
import type { EvergreenTimelineEvent } from '@/features/webinar/types';
import {
  getBackErrorCodes,
  hmsToSeconds,
  toScheduleError,
  type Hms,
} from '@/lib/evergreenInteractionSchedule';
import {
  resolvePurchaseNotificationDisplay,
  type PurchaseNotificationPayload,
} from '@/lib/purchaseNotificationDisplay';

/** Même borne que le back. */
export const MANUAL_NOTIFICATION_MESSAGE_MAX_LENGTH = 300;

export type PurchaseNotificationError =
  | 'MESSAGE_REQUIRED'
  | 'MESSAGE_TOO_LONG'
  | 'TIME_REQUIRED'
  | 'INVALID_TIME'
  | 'OUTSIDE_VIDEO'
  | 'VIDEO_NOT_READY'
  | 'SESSION_IN_PROGRESS'
  | 'OFFER_INVALID'
  | 'GENERIC';

export const PURCHASE_NOTIFICATION_ERRORS: readonly PurchaseNotificationError[] = [
  'MESSAGE_REQUIRED',
  'MESSAGE_TOO_LONG',
  'TIME_REQUIRED',
  'INVALID_TIME',
  'OUTSIDE_VIDEO',
  'VIDEO_NOT_READY',
  'SESSION_IN_PROGRESS',
  'OFFER_INVALID',
  'GENERIC',
];

const BACK_ERROR_TO_PURCHASE_NOTIFICATION_ERROR: Record<string, PurchaseNotificationError> = {
  'HTTP.ERROR.400.EVERGREEN.PURCHASE_NOTIFICATION.MESSAGE.REQUIRED': 'MESSAGE_REQUIRED',
  'HTTP.ERROR.400.EVERGREEN.PURCHASE_NOTIFICATION.MESSAGE.TOO_LONG': 'MESSAGE_TOO_LONG',
  'HTTP.ERROR.400.EVERGREEN.PURCHASE_NOTIFICATION.OFFER.INVALID': 'OFFER_INVALID',
};

export function getPurchaseNotifications(
  timeline: EvergreenTimelineEvent[],
): EvergreenTimelineEvent[] {
  return timeline
    .filter((event) => event.eventType === 'PURCHASE_NOTIFICATION')
    .sort(
      (a, b) =>
        a.startOffsetSeconds - b.startOffsetSeconds ||
        a.displayOrder - b.displayOrder ||
        a.id - b.id,
    );
}

/** `INLINE` en plus : l'ancien remplacement de timeline marquait aussi `MANUAL` les importées. */
export function isManualPurchaseNotification(event: EvergreenTimelineEvent): boolean {
  return (
    event.eventType === 'PURCHASE_NOTIFICATION' &&
    event.origin === 'MANUAL' &&
    event.resourceType === 'INLINE'
  );
}

/**
 * Les offres arrivent en parallèle de la timeline : tant qu'elles ne sont pas là, une liste vide ne
 * veut pas dire « aucune offre », et enregistrer enverrait null, ce qui effacerait le style d'une
 * notification déjà liée à une offre. Les deux drapeaux valent false avant le premier chargement :
 * l'éditeur n'est monté qu'après la timeline, une fois le chargement de la page lancé.
 */
export function areNotificationOffersReady(state: {
  pageLoading: boolean;
  offersLoading: boolean;
  offersError: string | null;
}): boolean {
  return !state.pageLoading && !state.offersLoading && state.offersError == null;
}

/** Offre dont le back a recopié le style dans une notification manuelle. */
export function getManualNotificationOfferId(payloadJson: string | null): number | null {
  if (!payloadJson) return null;
  try {
    const payload = JSON.parse(payloadJson) as { offerTemplateId?: unknown };
    return typeof payload.offerTemplateId === 'number' ? payload.offerTemplateId : null;
  } catch {
    return null;
  }
}

/**
 * Une nouvelle notification prend la première offre proposée. Une notification existante garde la
 * sienne tant qu'elle est encore proposée, sinon elle suit le thème du webinaire.
 */
export function resolveManualNotificationOfferId(
  event: EvergreenTimelineEvent | null,
  selectableOfferIds: readonly number[],
): number | null {
  if (event == null) return selectableOfferIds[0] ?? null;
  const storedOfferId = getManualNotificationOfferId(event.payloadJson);
  return storedOfferId != null && selectableOfferIds.includes(storedOfferId) ? storedOfferId : null;
}

/** Texte que verra le spectateur : même résolution que la bulle reçue pendant l'evergreen. */
export function getReplayedNotificationMessage(
  payloadJson: string | null,
  fallbackMessage: string,
): string {
  let payload: PurchaseNotificationPayload | null = null;
  if (payloadJson) {
    try {
      payload = JSON.parse(payloadJson) as PurchaseNotificationPayload;
    } catch {
      payload = null;
    }
  }
  const display = resolvePurchaseNotificationDisplay(
    { ...payload, evergreenReplay: true },
    { viewer: 'ATTENDEE', evergreenMessage: fallbackMessage },
  );
  return display?.message ?? fallbackMessage;
}

/** Même borne que les créneaux : la notification peut tomber pile à la fin de la vidéo. */
export function validatePurchaseNotificationOffset(
  offsetSeconds: number | null,
  videoDurationSeconds: number | null,
): PurchaseNotificationError | null {
  if (offsetSeconds == null) return 'INVALID_TIME';
  if (videoDurationSeconds != null && offsetSeconds > videoDurationSeconds) return 'OUTSIDE_VIDEO';
  return null;
}

/** Des champs horaires tous vides valent 00:00:00 pour `hmsToSeconds` : ici, c'est un oubli. */
export function validateManualPurchaseNotification(
  message: string,
  time: Hms,
  videoDurationSeconds: number | null,
): PurchaseNotificationError | null {
  const text = message.trim();
  if (text === '') return 'MESSAGE_REQUIRED';
  if (text.length > MANUAL_NOTIFICATION_MESSAGE_MAX_LENGTH) return 'MESSAGE_TOO_LONG';
  if ([time.h, time.m, time.s].every((value) => value.trim() === '')) return 'TIME_REQUIRED';
  return validatePurchaseNotificationOffset(hmsToSeconds(time), videoDurationSeconds);
}

function isPurchaseNotificationError(value: string): value is PurchaseNotificationError {
  return (PURCHASE_NOTIFICATION_ERRORS as readonly string[]).includes(value);
}

export function toPurchaseNotificationError(
  error: AppError | undefined,
): PurchaseNotificationError {
  for (const code of getBackErrorCodes(error)) {
    const messageError = BACK_ERROR_TO_PURCHASE_NOTIFICATION_ERROR[code];
    if (messageError) return messageError;
  }
  const scheduleError = toScheduleError(error);
  return isPurchaseNotificationError(scheduleError) ? scheduleError : 'GENERIC';
}
